import { useRef } from 'react';
import type React from 'react';
import { RentReportingAuthBoundary } from '../contexts/RentReportingAuthProvider';
import { EnrollmentRedirectHandler } from './EnrollmentRedirectHandler';
import { LoginMode, zillowWebLogin } from '@zillow/auth-core';
import { emitAuthStartEvent } from '../analytics/events';
import type { ClickstreamAuthTriggerSource } from '../analytics/events';
interface RentReportingBoundaryProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  authTriggerSource?: ClickstreamAuthTriggerSource;
}

export const RentReportingBoundary: React.FC<RentReportingBoundaryProps> = ({
  children,
  requireAuth = false,
  authTriggerSource = undefined,
}) => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  const hasEmittedAuthStart = useRef(false);

  if (!requireAuth) {
    return <EnrollmentRedirectHandler>{children}</EnrollmentRedirectHandler>;
  }

  return (
    <RentReportingAuthBoundary
      onUnauthorized={() => {
        const doLogin = async () => {
          // Only emit auth start event once
          if (authTriggerSource && !hasEmittedAuthStart.current) {
            emitAuthStartEvent(authTriggerSource);
            hasEmittedAuthStart.current = true;
          }

          // Build path with auth_trigger query param
          const currentUrl = new URL(window.location.href);
          if (authTriggerSource) {
            currentUrl.searchParams.set('auth_trigger', authTriggerSource);
          }
          const pathWithQuery = currentUrl.pathname + currentUrl.search;

          zillowWebLogin({
            loginMode: LoginMode.REDIRECT,
            redirectUri: isDevelopment
              ? `/rental/rent-credit-report/forward?${new URLSearchParams({
                  port: '3000',
                  protocol: 'https',
                  path: pathWithQuery,
                })}`
              : pathWithQuery,
            trackingConfig: {
              entryPoint: 'rent-reporting-web',
            },
            envConfig: { baseURLOverride: isDevelopment ? 'https://www.qa.zillow.net' : undefined },
          });
        };
        doLogin();

        return null;
      }}
    >
      <EnrollmentRedirectHandler>{children}</EnrollmentRedirectHandler>
    </RentReportingAuthBoundary>
  );
};
